import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import { monitoringService } from "@/services/monitoring.service";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

const STAGE_CONFIG: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  ONBOARDING: { label: "Onboarding", color: "text-blue-600", bg: "bg-blue-50" },
  GROWING: { label: "Growing", color: "text-emerald-600", bg: "bg-emerald-50" },
  MATURE: { label: "Mature", color: "text-green-600", bg: "bg-green-50" },
  STEADY: { label: "Steady", color: "text-slate-600", bg: "bg-slate-100" },
  DECLINING: {
    label: "Declining",
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  AT_RISK: { label: "At Risk", color: "text-red-600", bg: "bg-red-50" },
  CHURNED: { label: "Churned", color: "text-slate-400", bg: "bg-slate-50" },
  REACTIVATED: {
    label: "Reactivated",
    color: "text-violet-600",
    bg: "bg-violet-50",
  },
};

const SEGMENT_COLORS: Record<string, string> = {
  POWER: "bg-emerald-500",
  GROWING: "bg-blue-500",
  STEADY: "bg-slate-400",
  DECLINING: "bg-amber-500",
  DORMANT: "bg-slate-300",
  AT_RISK: "bg-red-500",
  NEW: "bg-violet-500",
};

export default function LifecycleDashboardPage() {
  const navigate = useNavigate();

  // Lifecycle overview
  const { data: lifecycle } = useQuery({
    queryKey: ["lifecycle", "overview"],
    queryFn: () => monitoringService.getLifecycleOverview(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  // Segment distribution
  const { data: segments } = useQuery({
    queryKey: ["segmentation", "distribution"],
    queryFn: () => monitoringService.getSegmentDistribution(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  // Segment definitions
  const { data: segmentDefs } = useQuery({
    queryKey: ["segmentation", "definitions"],
    queryFn: () => monitoringService.getSegmentDefinitions(),
    staleTime: Infinity,
  });

  const totalTenants =
    lifecycle?.distribution?.reduce(
      (sum: number, d: { count: number }) => sum + d.count,
      0,
    ) ?? 0;

  const totalSegmented =
    segments?.reduce((sum: number, s: { count: number }) => sum + s.count, 0) ??
    0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lifecycle & Segmentation"
        subtitle="Client lifecycle stages, segmentation, and recent transitions"
      />

      {/* Lifecycle stage distribution */}
      {lifecycle && (
        <>
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">
              Lifecycle Stages
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              {totalTenants} total tenants across{" "}
              {lifecycle.distribution.length} stages
            </p>

            {/* Stage bar chart */}
            <div className="mt-5 space-y-3">
              {lifecycle.distribution.map(
                (d: { stage: string; count: number }) => {
                  const cfg = STAGE_CONFIG[d.stage] ?? {
                    label: d.stage,
                    color: "text-slate-600",
                    bg: "bg-slate-100",
                  };
                  const pct =
                    totalTenants > 0
                      ? ((d.count / totalTenants) * 100).toFixed(1)
                      : "0";
                  return (
                    <div key={d.stage}>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`font-semibold ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        <span className="text-slate-400">
                          {d.count} ({pct}%)
                        </span>
                      </div>
                      <div className="mt-1 h-3 rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full transition-all ${cfg.bg}`}
                          style={{
                            width: `${totalTenants > 0 ? (d.count / totalTenants) * 100 : 0}%`,
                            minWidth: d.count > 0 ? 8 : 0,
                          }}
                        />
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </section>

          {/* Recent transitions */}
          {lifecycle.recentTransitions &&
            lifecycle.recentTransitions.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-900">
                  Recent Transitions
                </h2>
                <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
                  {lifecycle.recentTransitions.map(
                    (t: {
                      id: string;
                      tenantId: string;
                      fromStage: string;
                      toStage: string;
                      reason: string;
                      triggeredBy: string;
                      occurredAt: string;
                      tenant?: { name: string };
                    }) => {
                      const from = STAGE_CONFIG[t.fromStage];
                      const to = STAGE_CONFIG[t.toStage];
                      return (
                        <div
                          key={t.id}
                          className="flex items-center gap-3 rounded-xl border border-slate-100 p-3"
                        >
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/tenants/${t.tenantId}`)}
                              className="truncate text-xs font-semibold text-blue-600 hover:underline"
                            >
                              {t.tenant?.name ?? t.tenantId.slice(0, 12) + "…"}
                            </button>
                            <p className="mt-0.5 text-[11px] text-slate-400">
                              {t.reason} · {t.triggeredBy}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1 text-xs">
                            <span
                              className={`rounded-full px-2 py-0.5 font-semibold ${from?.bg ?? "bg-slate-100"} ${from?.color ?? "text-slate-500"}`}
                            >
                              {from?.label ?? t.fromStage}
                            </span>
                            <span className="text-slate-300">→</span>
                            <span
                              className={`rounded-full px-2 py-0.5 font-semibold ${to?.bg ?? "bg-slate-100"} ${to?.color ?? "text-slate-500"}`}
                            >
                              {to?.label ?? t.toStage}
                            </span>
                          </div>
                          <span className="shrink-0 text-[10px] text-slate-300">
                            {new Date(t.occurredAt).toLocaleDateString()}
                          </span>
                        </div>
                      );
                    },
                  )}
                </div>
              </section>
            )}
        </>
      )}

      {/* Segment distribution */}
      {segments && (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">
            Client Segments
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            Behaviour-based classification of {totalSegmented} tenants
          </p>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {segments.map(
              (s: { segment: string; count: number; percentage: number }) => {
                const def = segmentDefs?.find(
                  (d: { segment: string }) => d.segment === s.segment,
                );
                const barColor = SEGMENT_COLORS[s.segment] ?? "bg-slate-400";
                return (
                  <div
                    key={s.segment}
                    className="rounded-xl border border-slate-100 p-4"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2.5 w-2.5 rounded-full ${barColor}`}
                      />
                      <span className="text-xs font-semibold text-slate-700">
                        {def?.label ?? s.segment}
                      </span>
                    </div>
                    <p className="mt-2 text-2xl font-semibold text-slate-900">
                      {s.count}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      {s.percentage.toFixed(1)}% of total
                    </p>
                    {def?.description && (
                      <p className="mt-1 text-[10px] leading-tight text-slate-400">
                        {def.description}
                      </p>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </section>
      )}

      {/* Summary stats from lifecycle */}
      {lifecycle && (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Avg Health Score"
            value={(lifecycle.averageHealthScore ?? 0).toFixed(0)}
          />
          <StatCard
            label="Avg Days Since Active"
            value={(lifecycle.averageDaysSinceActive ?? 0).toFixed(0)}
          />
          <StatCard label="Total Tenants" value={String(totalTenants)} />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}
